import React from 'react';

type FeedSkeletonProps = {
  variant?: 'desktop' | 'mobile';
  count?: number;
};

const FeedPostCardSkeleton: React.FC<{ variant: 'desktop' | 'mobile' }> = ({ variant }) => {
  const isDesktop = variant === 'desktop';

  return (
    <div
      className={`relative overflow-hidden border border-[#E9E9E9] rounded-xl bg-white ${
        isDesktop ? 'w-4/5 h-[250px] p-4 my-4' : 'w-full min-h-[220px] p-4 mb-4'
      }`}
    >
      <div className="absolute inset-0 w-full h-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent z-10 pointer-events-none" />
      <div className="flex items-center gap-4 mb-4">
        <div className={`rounded-full bg-gray-200/70 animate-pulse ${isDesktop ? 'w-12 h-12' : 'w-10 h-10'}`} />
        <div className="flex-1 space-y-2">
          <div className={`h-4 bg-gray-200/70 rounded animate-pulse ${isDesktop ? 'w-32' : 'w-28'}`} />
          <div className="h-3 bg-gray-200/70 rounded w-24 animate-pulse" />
        </div>
        <div className={`h-10 bg-gray-200/70 rounded-md animate-pulse ${isDesktop ? 'w-[250px]' : 'w-24'}`} />
      </div>
      <div className="space-y-2 mt-6">
        <div className="h-3 bg-gray-200/70 rounded w-full animate-pulse" />
        <div className="h-3 bg-gray-200/70 rounded w-5/6 animate-pulse" />
        <div className="h-3 bg-gray-200/70 rounded w-2/3 animate-pulse" />
      </div>
      <div className="flex justify-around items-center mt-auto pt-6 border-t border-[#E9E9E9] absolute bottom-4 left-4 right-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="h-6 bg-gray-200/70 rounded w-20 animate-pulse" />
        ))}
      </div>
    </div>
  );
};

const FeedSkeleton: React.FC<FeedSkeletonProps> = ({ variant = 'desktop', count }) => {
  const isDesktop = variant === 'desktop';
  const cardCount = count ?? (isDesktop ? 3 : 4);

  return (
    <div
      role="status"
      aria-label="Loading feed posts"
      className={isDesktop ? 'ml-36 mt-16' : 'w-full px-4 pt-6'}
    >
      {Array.from({ length: cardCount }).map((_, index) => (
        <FeedPostCardSkeleton key={index} variant={variant} />
      ))}
      <span className="sr-only">Loading feed posts...</span>
    </div>
  );
};

export default FeedSkeleton;
